import classnames from 'classnames/bind'
import { useMemo, useRef } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextSize } from '~/@types/text-size'
import { GlobalTextStyling } from '~/@types/text-styling'

import Slider, { useSliderState } from '@unlikelystudio/react-slider'

import RichText, { RichTextBlocks } from '~/components/Abstracts/RichText'
import { ArrowLeftIcon, ArrowRightIcon } from '~/components/Icons'
import ProductCard, { ProductCardProps } from '~/components/Slices/ProductCard'
import SquaredCta, { SquaredCtaProps } from '~/components/UI/SquaredCta'

import { useSliceProvider } from '~/providers/SliceProvider'
import { useStyle } from '~/providers/StyleProvider'

import useTrackingProductImpression from '~/hooks/useTrackingProductImpresion'

import { GENERAL } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

const TITLE_SIZES = {
  [GlobalTextSize.Big]: GlobalTextPreset.Title22_30Haffer,
  [GlobalTextSize.Small]: GlobalTextPreset.Title14_18Haffer,
}

interface SliderNavigationProps {
  className?: string
  onPrev(): void
  onNext(): void
  isPrevVisible?: boolean
  isNextVisible?: boolean
  hasSeparator?: boolean
  iconClassName?: string
  theme?: 'light' | 'dark'
}

export interface ProductsSliderProps {
  className?: string
  title?: RichTextBlocks
  titleSize?: GlobalTextSize
  items?: ProductCardProps[]
  theme?: 'light' | 'dark'
  cta?: SquaredCtaProps
}

export function SliderNavigation({
  className,
  iconClassName,
  onNext,
  onPrev,
  isPrevVisible,
  isNextVisible,
  hasSeparator = true,
  theme = 'dark',
}: SliderNavigationProps) {
  const t = useTranslate()
  const colorTheme =
    theme === 'dark' ? GlobalThemeColors.Black : GlobalThemeColors.White

  return (
    <div className={cx(css.SliderNavigation, className)}>
      <button
        aria-label={t(GENERAL.PREVIOUS_SLIDER_BUTTON)}
        className={cx(css.iconContainer, { isVisible: isPrevVisible })}
        //avoid to click on the link parent
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onPrev()
        }}>
        <ArrowLeftIcon
          theme={colorTheme}
          className={cx(css.icon, iconClassName)}
        />
      </button>

      {hasSeparator && <span className={cx(css.separator, css[theme])} />}

      <button
        aria-label={t(GENERAL.NEXT_SLIDER_BUTTON)}
        className={cx(css.iconContainer, { isVisible: isNextVisible })}
        //avoid to click on the link parent
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onNext()
        }}>
        <ArrowRightIcon
          theme={colorTheme}
          className={cx(css.icon, iconClassName)}
        />
      </button>
    </div>
  )
}

const IMAGE_SIZES = [{ breakpoint: 'md', ratio: 6 / 24 }, { ratio: 12 / 24 }]

function ProductsSlider({
  className,
  title,
  titleSize,
  items,
  theme = 'dark',
  cta,
}: ProductsSliderProps) {
  const { position: slicePosition } = useSliceProvider()
  const customSliderRef = useRef()
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  const titleStyle = useStyle({
    textPreset: TITLE_SIZES[titleSize],
    color: theme === 'dark' ? GlobalThemeColors.Black : GlobalThemeColors.White,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const [
    { nextSlide, prevSlide, slideIndex, enableDrag, maxSlideIndex },
    setSliderState,
  ] = useSliderState()

  const hasSlider = items && items.length > 0

  const { isPrevVisible, isNextVisible } = useMemo(
    () => ({
      isPrevVisible: enableDrag && slideIndex !== 0,
      isNextVisible: enableDrag && slideIndex < maxSlideIndex - 1,
    }),
    [enableDrag, maxSlideIndex, slideIndex],
  )

  const { ref } = useTrackingProductImpression(items)

  if (items?.length === 0) {
    return null
  }

  return (
    <div ref={ref} className={cx(css.ProductsSlider, className)}>
      <div className={cx(css.container, gridStyle)}>
        <div className={cx(css.header)}>
          <RichText className={cx(css.title, titleStyle)} render={title} />

          {hasSlider && items.length > 4 && (
            <SliderNavigation
              className={cx(css.navigation)}
              onPrev={prevSlide}
              onNext={nextSlide}
              isPrevVisible={isPrevVisible}
              isNextVisible={isNextVisible}
              theme={theme}
            />
          )}
        </div>

        {hasSlider && (
          <Slider
            className={css.slider}
            setSliderState={setSliderState}
            customSliderRef={customSliderRef}
            autoBlockSlideIndexChange={true}
            snap>
            {items?.map((card, index) => {
              return (
                <ProductCard
                  key={`${card.name}_${index}`}
                  className={cx(css.card, css.cardWidth)}
                  sizesFromBreakpoints={IMAGE_SIZES}
                  priority={slicePosition === 1 && index < 5}
                  theme={theme}
                  {...card}
                />
              )
            })}
          </Slider>
        )}
        {cta && (
          <div className={css.ctaContainer}>
            <SquaredCta {...cta} theme={GlobalThemeColors.White} />
          </div>
        )}
      </div>
    </div>
  )
}

ProductsSlider.defaultProps = {}

export default ProductsSlider
